// Experiments (A/B testing) domain helper. Organised as: constants -> types -> validation ->
// topic parsing -> the `var` cookie -> deterministic bucketing -> the RSC request protocol.
// The middleware-only half (bot classification, request-state building) lives in
// experiment-helper.server.ts so the bot UA list never ships in the client bundle.
//
// Safe in the edge middleware, in server components and in client renderers alike (mirrors
// tile-country-visibility.ts) — the one runtime dependency, the cookie reader, no-ops outside the
// browser. No node builtins, no next/* imports, no crypto.
//
// Crystallize contract (owned by the content team, confirmed against the tenant on 2026-08-12):
//   - Shape "Content Wrapper" whose single top-level component is the PIECE
//     "content-wrapper-reactive-content". Inside the piece: a "reactive-trigger" selection (the
//     Experiments option's value is "var"; its key is the human label) and a "reactive-content"
//     item relation holding the candidate tiles.
//   - Topic map root "/experiments/". Each experiment is a topic node whose NAME IS the experiment
//     name; its children are the arms: the control ("original" or the tenant's suffixed
//     "original-<x>") plus one topic per variant.
//   - A candidate tile carries the arm topic it belongs to ("/experiments/<name>/<variant>").
//     A candidate without an experiment topic is treated as part of the control arm. Arm topics live
//     on the candidate tiles ONLY — the page document itself carries no experiment topic.

import { getCookieValue } from '@/utils/cookie-helper';
import { EXPERIMENT_VAR_COOKIE_NAME } from '@/utils/constants';

// --- Constants -----------------------------------------------------------------------------------

// The middleware -> RSC channel, same pattern as `x-url`.
export const EXPERIMENT_HEADER_NAME = 'x-experiment';

// Crystallize contract ids (see the header of this file).
export const EXPERIMENT_TOPIC_PATH_PREFIX = '/experiments/';
export const CONTENT_WRAPPER_SHAPE_ID = 'content-wrapper';
export const EXPERIMENT_WRAPPER_TRIGGER_VALUE = 'var';
// Component ids INSIDE the content-wrapper-reactive-content piece (see the `experimentWrapper`
// alias in __tile-fragments.gql).
export const EXPERIMENT_TRIGGER_COMPONENT_ID = 'reactive-trigger';
export const EXPERIMENT_CANDIDATES_COMPONENT_ID = 'reactive-content';

// The control arm's canonical name. In the topic tree the tenant convention suffixes it per
// experiment ("original-a" under "experiment-name-a"), so detection goes through
// isOriginalVariant() rather than an exact comparison.
export const EXPERIMENT_ORIGINAL_VARIANT = 'original';

// --- Types ---------------------------------------------------------------------------------------

// One recorded arm choice: the payload of the `var` cookie entries, the exposure POST and the
// tracking-service contract.
export interface ExperimentAssignment {
  name: string;
  variant: string;
}

// Experiment fields carried by analytics payloads (GTM dataLayer and exponea), so a conversion can
// be attributed to the arm the visitor was in.
export interface ExperimentTrackingProps {
  experiment_name?: string;
  experiment_variant?: string;
}

// The per-request state the middleware serialises into the x-experiment header.
export interface ExperimentRequestState {
  // Mixed 32-bit hash of `nuki.sid`. Stable for the whole session, which is what makes the arm
  // stable across page 1 and page 2 before any cookie exists.
  bucketSeed: number;
  isBot: boolean;
  // The `?var` value when it carries one, used as a QA force-variant override.
  override?: string;
  // Assignments already recorded in the `var` cookie for this session.
  assignments: ExperimentAssignment[];
}

// Fail-safe state: seed 0 deterministically maps to arm 0, i.e. the original. A missing or corrupt
// header therefore degrades to "everyone sees the control content", never to a crash.
export const EMPTY_EXPERIMENT_REQUEST_STATE: ExperimentRequestState = {
  bucketSeed: 0,
  isBot: false,
  assignments: [],
};

interface TopicReference {
  path: string;
}

// --- Validation ------------------------------------------------------------------------------------

// Mirrors the /experiments handler contract of the tracking service exactly: the same regex and the
// same length caps, so a payload accepted anywhere in this app can never be rejected there (and vice
// versa). Names/variants flow on into Mongo keys and a MySQL column, hence the conservative charset.
export const EXPERIMENT_NAME_MAX_LENGTH = 100;
export const EXPERIMENT_VARIANT_MAX_LENGTH = 50;
export const EXPERIMENT_SAFE_VALUE_PATTERN = /^[a-zA-Z0-9_-]+$/;

// Hard cap on the assignment list everywhere it crosses a trust boundary (the `var` cookie, the
// x-experiment header, the confirm server action). The cookie and the `?var` parameter are
// attacker-writable and the header is echoed on every response, so nothing unbounded may pass.
export const MAX_EXPERIMENT_ASSIGNMENTS = 20;

export function isValidExperimentAssignment(assignment: ExperimentAssignment): boolean {
  return (
    assignment.name.length <= EXPERIMENT_NAME_MAX_LENGTH &&
    assignment.variant.length <= EXPERIMENT_VARIANT_MAX_LENGTH &&
    EXPERIMENT_SAFE_VALUE_PATTERN.test(assignment.name) &&
    EXPERIMENT_SAFE_VALUE_PATTERN.test(assignment.variant)
  );
}

export function sanitizeExperimentAssignments(
  assignments: ExperimentAssignment[],
): ExperimentAssignment[] {
  return assignments.filter(isValidExperimentAssignment).slice(0, MAX_EXPERIMENT_ASSIGNMENTS);
}

// A usable QA override is a variant name; anything longer or outside the charset can never match an
// arm, so it is dropped at the boundary instead of being carried (and echoed) any further.
export function sanitizeExperimentOverride(value: string | null | undefined): string | undefined {
  if (!value || value.length > EXPERIMENT_VARIANT_MAX_LENGTH) {
    return undefined;
  }

  return EXPERIMENT_SAFE_VALUE_PATTERN.test(value) ? value : undefined;
}

// --- Topic parsing -------------------------------------------------------------------------------

export function isOriginalVariant(variant: string): boolean {
  return (
    variant === EXPERIMENT_ORIGINAL_VARIANT || variant.startsWith(`${EXPERIMENT_ORIGINAL_VARIANT}-`)
  );
}

// Extracts the assignment encoded in an experiment topic path, i.e. the first topic that looks like
// "/experiments/<name>/<variant>". Deeper paths are ignored beyond the first two segments so an
// editor adding sub-topics never changes the arm identity. Returns undefined when no topic
// addresses an experiment arm (the common case: an untagged tile).
export function parseExperimentTopic(
  topics: TopicReference[] | null | undefined,
): ExperimentAssignment | undefined {
  const experimentPaths = (topics ?? [])
    .map((topic) => topic.path.toLowerCase())
    .filter((path) => path.startsWith(EXPERIMENT_TOPIC_PATH_PREFIX));

  return experimentPaths
    .map((path) => {
      const [name, variant] = path.slice(EXPERIMENT_TOPIC_PATH_PREFIX.length).split('/');

      return name && variant ? { name, variant } : undefined;
    })
    .find((assignment) => assignment !== undefined);
}

// --- The `var` cookie ----------------------------------------------------------------------------

// Runtime guard for values coming off the wire (cookie payload, server-action argument).
export function isExperimentAssignment(value: unknown): value is ExperimentAssignment {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<ExperimentAssignment>;

  return (
    typeof candidate.name === 'string' &&
    candidate.name.length > 0 &&
    typeof candidate.variant === 'string' &&
    candidate.variant.length > 0
  );
}

// The `var` cookie holds an ARRAY of assignments — one entry per experiment the session has been
// exposed to. Page-scoped experiments therefore accumulate instead of overwriting each other; the
// normal single-experiment case is simply an array of length 1.
export function isExperimentAssignmentArray(value: unknown): value is ExperimentAssignment[] {
  return Array.isArray(value) && value.every(isExperimentAssignment);
}

// Parses a JSON-encoded assignment array — the `var` cookie payload, wherever it was read
// (document.cookie in the browser, the request cookie in the middleware). Never throws: a
// hand-edited or truncated payload yields no assignments, so the session simply re-rolls. Entries
// violating the validation contract are dropped and the list is capped, because the cookie is
// attacker-writable and its content is echoed into the x-experiment response header.
export function parseExperimentAssignments(raw: string | null | undefined): ExperimentAssignment[] {
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    return isExperimentAssignmentArray(parsed) ? sanitizeExperimentAssignments(parsed) : [];
  } catch {
    return [];
  }
}

// Reads the `var` cookie in the browser. A malformed cookie degrades to "no experiment" rather
// than breaking a conversion event.
export function readExperimentAssignments(): ExperimentAssignment[] {
  return parseExperimentAssignments(getCookieValue(EXPERIMENT_VAR_COOKIE_NAME));
}

// Rendered-but-not-yet-confirmed assignments of the current document. The `var` cookie only exists
// once the confirm round trip has landed, so tracking events fired before that (the landing page's
// view_item, a fast add_to_cart) would otherwise carry no attribution — precisely the events an
// experiment exists to measure. Browser-only: on the server this module state would be shared
// across requests.
const pendingRenderedAssignments = new Map<string, string>();

// Called at render time by ExperimentAssignmentEffect — render always precedes every mount effect,
// so any tracking event of the same document sees the assignment. Idempotent, so safe under strict
// mode's double render.
export function registerRenderedExperimentAssignments(assignments: ExperimentAssignment[]): void {
  if (typeof document === 'undefined') {
    return;
  }

  assignments.forEach((assignment) => {
    pendingRenderedAssignments.set(assignment.name, assignment.variant);
  });
}

/**
 * Experiment fields for an analytics payload; `{}` when the session is in no experiment, so the keys
 * are simply absent instead of being sent empty. Confirmed (cookie) assignments and just-rendered
 * ones are merged by experiment name; the rendered arm wins, because it is what the visitor
 * actually sees.
 *
 * With several page-scoped assignments the values are comma-joined in matching order (name[i] pairs
 * with variant[i]). That is unambiguous because both are restricted to `[a-zA-Z0-9_-]`, and the
 * single-experiment case — the norm — stays a plain scalar. Metabase's sessionId join remains the
 * ground truth for multi-experiment analysis.
 */
export function getExperimentTrackingProps(): ExperimentTrackingProps {
  const merged = new Map<string, string>();

  readExperimentAssignments().forEach((assignment) => {
    merged.set(assignment.name, assignment.variant);
  });
  pendingRenderedAssignments.forEach((variant, name) => {
    merged.set(name, variant);
  });

  if (merged.size === 0) {
    return {};
  }

  return {
    experiment_name: Array.from(merged.keys()).join(','),
    experiment_variant: Array.from(merged.values()).join(','),
  };
}

// --- Deterministic bucketing ---------------------------------------------------------------------

/* eslint-disable no-bitwise -- FNV-1a is defined in terms of XOR and shifts; there is no
   arithmetic formulation of it. Scoped to the two hash primitives below. */

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

// FNV-1a with a caller-supplied basis so a hash can be salted by seeding it with a previous hash.
// `Math.imul` keeps the multiply in 32-bit space. Iterates by index (code points, per S7758) — for
// the actual inputs (uuid sids, [a-zA-Z0-9_-] names, both pure ASCII) this hashes exactly the same
// values as the classic code-unit variant, so bucketing stays stable.
function hashFnv1a32(input: string, basis: number): number {
  let hash = basis >>> 0;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.codePointAt(index) ?? 0;
    hash = Math.imul(hash, FNV_PRIME) >>> 0;
  }

  return hash >>> 0;
}

// Avalanche finaliser (murmur3's fmix32). FNV-1a's low bits are nearly linear in the input's low
// bits, so the raw hash must never be reduced by parity or a small modulus — and a single
// `hash ^ (hash >>> 16)` fold still leaves last-character name differences (exp-a / exp-b)
// under-diffused; the full finaliser is load-bearing for the per-experiment salt.
function avalancheBits32(hash: number): number {
  let mixed = (hash ^ (hash >>> 16)) >>> 0;
  mixed = Math.imul(mixed, 0x85ebca6b) >>> 0;
  mixed = (mixed ^ (mixed >>> 13)) >>> 0;
  mixed = Math.imul(mixed, 0xc2b2ae35) >>> 0;
  mixed = (mixed ^ (mixed >>> 16)) >>> 0;

  return mixed;
}

/* eslint-enable no-bitwise */

// Derives the per-session bucket seed from `nuki.sid`. A missing sid yields 0, which resolves to the
// original for every experiment.
export function bucketSeedFromSessionId(sessionId: string | null | undefined): number {
  if (!sessionId) {
    return 0;
  }

  return avalancheBits32(hashFnv1a32(sessionId, FNV_OFFSET_BASIS));
}

// Picks the arm index for one experiment. Salting by experiment name (the seed is the basis, the
// name is the input) decorrelates concurrent experiments: two experiments on different pages must
// not deal the same visitors to the same side, or they statistically confound each other.
// `% armCount` gives an even split for any N: exactly 50:50 for two arms, even thirds for three.
export function armFromSeed(seed: number, experimentName: string, armCount: number): number {
  if (armCount <= 1) {
    return 0;
  }

  // Seed 0 = unknown visitor (no sid, missing or corrupt header): pin to the control arm.
  if (seed === 0) {
    return 0;
  }

  return avalancheBits32(hashFnv1a32(experimentName, seed)) % armCount;
}

// --- Middleware -> RSC request protocol ----------------------------------------------------------
//
// The middleware computes everything that depends on the raw request (bot classification, the
// session-derived bucket seed, the `?var` parameter, the existing `var` cookie) via
// buildExperimentRequestState in experiment-helper.server.ts, serialises it into a single response
// header and forwards it. Server components read it back here with `headers()`, exactly the channel
// `x-url` already uses. Keeping the whole request state in one header means the arm is resolved
// from identical inputs on the server and on the client, so SSR and hydration cannot disagree.

// Reads the header back in an RSC. Anything unexpected degrades to the fail-safe state; the same
// value caps as the middleware apply, so a spoofed header cannot smuggle unbounded data inward.
export function parseExperimentHeader(raw: string | null | undefined): ExperimentRequestState {
  if (!raw) {
    return EMPTY_EXPERIMENT_REQUEST_STATE;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null) {
      return EMPTY_EXPERIMENT_REQUEST_STATE;
    }

    const candidate = parsed as Partial<ExperimentRequestState>;
    const override =
      typeof candidate.override === 'string'
        ? sanitizeExperimentOverride(candidate.override)
        : undefined;

    return {
      bucketSeed: typeof candidate.bucketSeed === 'number' ? candidate.bucketSeed : 0,
      isBot: candidate.isBot === true,
      ...(override ? { override } : {}),
      assignments: isExperimentAssignmentArray(candidate.assignments)
        ? sanitizeExperimentAssignments(candidate.assignments)
        : [],
    };
  } catch {
    return EMPTY_EXPERIMENT_REQUEST_STATE;
  }
}
